import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ExplorerCustomTree} from '@catapp/shared/explorer-custom-tree';
import {FeatureTypeService} from '@catcore/services';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {Feature} from '@catcore/services/feature/feature';
import {ModelTypes} from '@catcore/utils/json-schema';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {itemFactory} from '@catfeatures/shared/utils';
import {FeatureChilds, FeatureExplorerAction} from '@catfeatures/types';
import {BreadcrumbService} from '@catmodules/widgets/breadcrumb/services/breadcrumb.service';
import {TreeNodeInfo} from '@catui/types';
import 'rxjs/add/observable/pairs';
import {isNullOrUndefined} from 'util';

/**
 * Component that provides the backbone for displaying detailed information for a Feature
 *
 */
@Component({
  templateUrl: './feature-details.component.html',
  styleUrls: ['./feature-details.component.scss'],
})
export class FeatureDetailsComponent extends ExplorerCustomTree implements OnInit {
  private currentFeature: Feature;
  enableSearch = false;

  constructor(route: ActivatedRoute,
              resourceService: GenericResourceService,
              private featureService: FeatureTypeService,
              schemaService: EntitySchemaService,
              breadcrumbService: BreadcrumbService,
              router: Router) {
    super(route, resourceService, schemaService, breadcrumbService, router);
  }

  ngOnInit() {
    this.featureService
      .getCurrent()
      .subscribe((feature: Feature) => {

        const allowRefreshFeature = !isNullOrUndefined(feature) && this.isCurrentFeature(feature);
        const allowInitFeature = !isNullOrUndefined(feature) && !this.isCurrentFeature(feature);
        if (!feature) {
          return;
        }

        if (allowRefreshFeature) {
          this.refreshFeature(feature);
        }

        if (allowInitFeature) {
          this.initFeature(feature);
        }
      });

  }

  /**
   *
   * @param $event
   * @param {TreeNodeInfo} nodeInfo
   */
  addNewItem($event, nodeInfo: TreeNodeInfo) {
    $event.stopPropagation();
    this.navigateToTargetRoute(nodeInfo, true);

  }

  /**
   * Returns true if all current conditions, based on the provided TreeNodeInfo, are fullfiled successfuly
   * @param {TreeNodeInfo} item
   * @returns {boolean}
   */
  doDisplayNodeControls(item: TreeNodeInfo): boolean {
    let doAllow = !isNullOrUndefined(item.item.children);
    doAllow = doAllow && this.doAllowNewItem(item) || this.doAllowDisplayLanding(item);
    return doAllow;
  }

  doAllowNewItem(node: TreeNodeInfo): boolean {
    return node.item.allowedActions.includes(FeatureExplorerAction.create);
  }

  doAllowDisplayLanding(node: TreeNodeInfo): boolean {
    return node.item.allowedActions.includes(FeatureExplorerAction.info);
  }

  getAllowCreateAction() {
    return FeatureExplorerAction.create;
  }

  getAllowInfoAction() {
    return FeatureExplorerAction.info;
  }

  private updateExplorerItems(childInfos: FeatureChilds) {
    const factory = itemFactory(this.treeNodeFactory, this.defaultFactoryOptions);
    this.explorerItemsDirectForward = [
      {
        label: 'Feature Properties',
        value: {route: {command: 'properties'}},
        expanded: false,
        allowedActions: [],
      },
      {
        label: 'Project Usages',
        value: {route: {command: 'project-usages'}},
        expanded: false,
        allowedActions: [],
      }
    ];

    this.explorerItems = [
      this.explorerItemsDirectForward[0],
      factory({
        data: childInfos.featureUnits,
        type: ModelTypes.Unit,
        command: 'units',
        allowedActions: [FeatureExplorerAction.create],
        filter: ['network_interfaces', 'ggw_implementation_types', 'ggw_implementation_type', 'action:update'],
      }),
      factory({
        data: childInfos.featureServices,
        type: ModelTypes.Service,
        command: 'services',
        allowedActions: [FeatureExplorerAction.create],
        filter: ['action:update'],
      }),
      factory({
        data: childInfos.featureGgwDevices,
        type: ModelTypes.GgwDeviceType,
        command: 'ggw-devices',
        description: 'GUI Gateway Devices',
        allowedActions: [FeatureExplorerAction.create],
        filter: ['parameters', 'ggw_fixed_routes_in', 'ggw_fixed_routes_out', 'icon_roles', 'icon_x', 'icon_y', 'evid_vid_type_selections', 'gui_pages', 'action:update'],
      }),
      factory({
        data: childInfos.featureGgwFixedRoutes,
        type: ModelTypes.GgwFixedRoute,
        command: 'fixed-routes',
        allowedActions: [FeatureExplorerAction.create],
      }),
      factory({
        data: childInfos.featureGgwDynRouteTemplates,
        type: ModelTypes.FeatureGgwDynamicRouteTemplates,
        command: 'dyn-route-templates',
        allowedActions: [FeatureExplorerAction.create]
      }),
      factory({
        data: childInfos.featureEvidFunctions,
        type: ModelTypes.FeatureEvidFunction,
        command: 'evid-functions',
        allowedActions: [FeatureExplorerAction.create],
        filter: ['action:update', 'linked_ports', 'linked_units', 'linked_vids', 'linked_services', 'linked_gui_zones'],
      }),
      factory({
        data: childInfos.featurePorts,
        type: ModelTypes.Port,
        command: 'ports',
        allowedActions: [FeatureExplorerAction.info],
        filter: ['port_connection_types1'],
      }),
      factory({
        data: childInfos.featureOptionTokens,
        type: ModelTypes.FeatureOptionTokens,
        command: 'option-tokens',
        allowedActions: [FeatureExplorerAction.create],
      }),
      factory({
        data: childInfos.featureAvodCatalogs,
        type: ModelTypes.FeatureAvodCatalogs,
        command: 'avod-catalog-create',
        allowedActions: [FeatureExplorerAction.create],
      }),
      this.explorerItemsDirectForward[1],
    ];
  }

  private setFeature(feature: Feature) {
    this.currentFeature = feature;
  }

  private isCurrentFeature(feature: Feature) {
    return !isNullOrUndefined(this.currentFeature) && this.currentFeature.id === feature.id;
  }

  /**
   * Refresh the state for a already selected Feature, but it's data changed over time
   * Reset the navigation tree and query params to the initial of a fresh selected feature
   *
   *
   * @param {Feature} feature
   */
  private refreshFeature(feature) {
    this.setFeature(feature);
    this.refreshExplorerForFeature(feature);
    this.refreshNavigationalState(true);
  }

  /**
   * Initializing all data for a new Feature item
   * Reset the navigation tree and query params to the initial of a fresh selected feature
   *
   *
   * @param {Feature} feature
   */
  private initFeature(feature: Feature) {
    this.setFeature(feature);
    this.refreshExplorerForFeature(feature);
    this.initNavigationalState(true);
  }

  private refreshExplorerForFeature(feature: Feature) {
    this.updateExplorerItems({
      featureUnits: feature._links.unit_types ? feature._links.unit_types : [],
      featureServices: feature._links['action:services'],
      featureGgwDevices: feature._links.ggw_devices ? feature._links.ggw_devices : [],
      featureGgwDynRouteTemplates: feature._links.ggw_dynamic_route_templates ? feature._links.ggw_dynamic_route_templates : [],
      featureGgwFixedRoutes: feature._links['action:ggw-fixed-routes'],
      featurePorts: feature._links['action:ports'],
      featureOptionTokens: feature._embedded.option_tokens ? feature._embedded.option_tokens : [],
      featureEvidFunctions: feature._links.evid_function_types ? feature._links.evid_function_types : [],
      featureAvodCatalogs: feature._links.avod_catalogs ? feature._links.avod_catalogs : [],
    });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/features/components/feature-details/feature-details.component.ts